import { useShallow } from "zustand/shallow";
import createClient from "openapi-fetch";
import { type paths as AdminPaths } from "@njbsoft/types/admin";
import { createApiClient, RefreshManager } from "@njbsoft/api-client";
import { useAdminStore } from "@/store";
import { queryClient } from "@/lib/query-client";
import { readPersistedSessionTokens } from "@/lib/persisted-session";

const API_URL = import.meta.env.VITE_API_URL;

/** Tear the local session down and hard-navigate to login. Shared by the
 *  session-end paths that never touch the Sign Out button: a rejected refresh,
 *  a 401 with no recovery, and a sibling tab's sign-out reaching
 *  `lib/session-sync`. The hard navigation is intentional -- a full page reload
 *  provides a clean slate with no risk of stale state leaking. */
export function clearAdminSession(): void {
  useAdminStore.getState().clearAuth();
  queryClient.clear();
  window.location.href = "/login";
}

// Centralized refresh manager -- shared by API client interceptor and auth
// guard.
//
// The last three options are the cross-tab layer: the manager takes an
// exclusive Web Lock around every refresh and, inside it, adopts a sibling's
// rotation from the persisted store when that pair is stamped LATER than the
// one captured here — because a tab replaying a token its sibling rotated more
// than the server's grace window ago reads as token theft and revokes the whole
// rotation family.
export const refreshManager = new RefreshManager({
  baseUrl: API_URL,
  getRefreshToken: () => useAdminStore.getState().refreshToken,
  onTokenRefreshed: (accessToken, refreshToken, expiresAt) => {
    const state = useAdminStore.getState();
    state.setAuth(accessToken, state.user, refreshToken);
    state.setExpiresAt(new Date(expiresAt).getTime());
  },
  onRefreshFailed: clearAdminSession,
  getPersistedTokens: readPersistedSessionTokens,
  adoptTokens: (tokens) => {
    useAdminStore.getState().adoptTokens(tokens);
  },
  getTokenStamp: () => useAdminStore.getState().rotatedAt,
});

// Create API client with token getter and RefreshManager
export const apiClient = createApiClient({
  baseUrl: API_URL,
  getToken: () => useAdminStore.getState().token,
  refreshManager,
  onUnauthorized: clearAdminSession,
});

// Admin-specific API client typed against admin OpenAPI spec paths.
// API_URL already includes /api/v1, so we only append /admin.
// Uses the same auth interceptor pattern as the main client via @njbsoft/api-client.
export const adminApiClient = createClient<AdminPaths>({
  baseUrl: `${API_URL}/admin`,
  credentials: "include",
});

// Per-request clone map: each request gets its own clone so concurrent 401
// retries replay the correct body. WeakMap ensures clones are GC'd automatically.
const adminRequestClones = new WeakMap<Request, Request>();
adminApiClient.use({
  async onRequest({ request }) {
    if (refreshManager.isRefreshing) {
      await refreshManager.refresh();
    }
    const token = useAdminStore.getState().token;
    if (token) {
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    adminRequestClones.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401) return response;
    const success = await refreshManager.refresh();
    const clone = adminRequestClones.get(request);
    if (success && clone) {
      const token = useAdminStore.getState().token;
      if (token) {
        clone.headers.set("Authorization", `Bearer ${token}`);
      }
      return fetch(clone, { credentials: "include" });
    }
    return response;
  },
});

// Hook for components
export function useApiClient() {
  return apiClient;
}

// Auth context for router
export function useAuth() {
  return useAdminStore(
    useShallow((s) => ({
      token: s.token,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt,
      user: s.user,
      isAuthenticated: s.isAuthenticated,
      setAuth: s.setAuth,
      setRefreshToken: s.setRefreshToken,
      setExpiresAt: s.setExpiresAt,
      rotatedAt: s.rotatedAt,
      adoptTokens: s.adoptTokens,
      setEmailVerified: s.setEmailVerified,
      clearAuth: s.clearAuth,
      mfaSessionToken: s.mfaSessionToken,
      mfaPending: s.mfaPending,
      mfaMethods: s.mfaMethods,
      setMfaChallenge: s.setMfaChallenge,
      clearMfaChallenge: s.clearMfaChallenge,
    })),
  );
}
